import React, { useState, useEffect } from 'react';
import { ChevronDown, Building2, FileText, Users, Shield, CheckCircle, Scale, UserCheck, Link as LinkIcon, Lock, RefreshCw } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';

export default function TermsAndConditions() {
    const [expandedSection, setExpandedSection] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sections = [
        {
            id: 'about',
            title: 'About Landmarks Realty',
            icon: Building2,
            color: 'from-[#3B2B9A] to-[#5A47D8]',
            content: `Landmarks Realty was established on 19 January 2017 as a real estate consulting and advisory firm. Our role is to guide homebuyers, investors, and property searchers with informed guidance on residential and commercial real estate. We focus on helping clients understand market dynamics, available options, and procedural aspects of property transactions. While we provide structured guidance and market insights, the final decision regarding any property purchase, sale, lease, or investment remains entirely with the client.`
        },
        {
            id: 'interpretation',
            title: 'Interpretation',
            icon: FileText,
            color: 'from-blue-500 to-cyan-500',
            content: `References to "you," "your," or "user" apply to any individual or legal entity accessing or using the website. Section headings are included for convenience only and do not affect interpretation. Words used in the singular include the plural and vice versa, unless the context requires otherwise.

Definitions:
• Account: The registered user profile created to access specific features of the website.
• The Company: Referred to as "Landmarks Realty," "we," "us," or "our," means Landmarks Realty, a real estate consulting firm operating in India.
• Content: Includes all text, images, graphics, videos, and materials made available on the website.
• Device: Any electronic device used to access the website, including computers, mobile phones, or tablets.
• Personal Data: Information that identifies or can reasonably be linked to an individual.
• Website: The Landmarks Realty website and any related digital services.
• Services: Real estate consulting services offered by the Company.
• Usage Data: Data collected automatically through use of the website, including interaction and access details.
• User: Any person who accesses or uses the website.`
        },
        {
            id: 'services',
            title: 'Services',
            icon: CheckCircle,
            color: 'from-green-500 to-emerald-500',
            content: `"Services" include real estate consulting and advisory offerings provided through the website.

These may include, but are not limited to:
• Property Advisory: Providing guidance on buying, selling, leasing, or investing in residential and commercial real estate.
• Property Listings and Promotion: Enabling property owners and developers to showcase listings and marketing materials.
• Transaction Support: Assisting with negotiations, documentation coordination, title checks, and compliance support.
• Market Research and Insights: Offering pricing trends, location analysis, and valuation inputs.
• Investment Consulting: Advising clients on property-based investment planning and risk considerations.
• Loan and Financing Support: Connecting users with lending institutions and financing partners.

Landmarks Realty reserves the right to revise, suspend, or discontinue any Service at its discretion without prior notice.`
        },
        {
            id: 'eligibility',
            title: 'Eligibility',
            icon: UserCheck,
            color: 'from-purple-500 to-pink-500',
            content: `You must be at least 18 years of age to use the website. By accessing the website, you confirm that you are legally capable of entering into a binding agreement under applicable Indian law.`
        },
        {
            id: 'user-responsibilities',
            title: 'User Responsibilities',
            icon: Users,
            color: 'from-orange-500 to-red-500',
            content: `By using the website, you agree to the following obligations:

Website/Social Media Platforms Security: You will not attempt to damage, disrupt, or compromise the security or functionality of the website. Introducing harmful code or software is strictly prohibited.

Automated Access Restrictions: You may not use bots, crawlers, scraping tools, or automated systems to extract data or interact with the website.

Intellectual Property Respect: All content on the website is protected by applicable intellectual property laws. You may not copy, distribute, modify, or reuse any material without written authorization.

Commercial Use Restrictions: You may not resell, sublicense, or commercially exploit any content or services obtained through the website without prior consent.

Fair Use: You agree not to overload, impair, or interfere with the website's performance or with other users' access.

Legal Compliance: You will use the website in accordance with all applicable laws and these Terms. Any fraudulent or unlawful activity is prohibited.

Authorized Access: You may only access areas of the website for which you have permission. Attempts to gain unauthorized access are not allowed.

Failure to comply may result in suspension, termination, or legal action.`
        },
        {
            id: 'liability',
            title: 'Limitation of Liability',
            icon: Shield,
            color: 'from-red-500 to-rose-500',
            content: `Landmarks Realty shall not be responsible for losses or damages arising from the use of the website or services, including inaccuracies in listings, third-party conduct, or technical issues, unless otherwise agreed in writing.`
        },
        {
            id: 'third-party',
            title: 'Third-Party Links',
            icon: LinkIcon,
            color: 'from-indigo-500 to-purple-500',
            content: `The website may include links to third-party websites or property listings. Landmarks Realty does not control or endorse these sites and is not responsible for their content or practices.`
        },
        {
            id: 'account',
            title: 'Account Registration',
            icon: Lock,
            color: 'from-teal-500 to-cyan-500',
            content: `Certain features may require account registration. You agree to provide accurate and current information. We reserve the right to suspend or terminate accounts that provide false information or violate these Terms.`
        },
        {
            id: 'privacy',
            title: 'Privacy and Data Protection',
            icon: Shield,
            color: 'from-blue-600 to-indigo-600',
            content: `Your use of the website is governed by our Privacy Policy, which outlines how personal data is collected, used, and safeguarded.`
        },
        {
            id: 'amendments',
            title: 'Amendments',
            icon: RefreshCw,
            color: 'from-amber-500 to-orange-500',
            content: `Landmarks Realty may update these Terms at any time. Continued use of the website after changes are posted constitutes acceptance of the revised Terms.`
        }
    ];

    const toggleSection = (id) => {
        setExpandedSection(expandedSection === id ? null : id);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900">
            <Navbar />

            {/* Enhanced Header */}
            <div className="pt-24 relative overflow-hidden bg-gradient-to-br from-[#3B2B9A] via-[#5A47D8] to-[#7B68EE]">
                {/* Animated Background Elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-white/10 rounded-full blur-3xl animate-pulse delay-700"></div>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
                </div>

                {/* Grid Pattern */}
                <div className="absolute inset-0 opacity-5" style={{
                    backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                       linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                    backgroundSize: '50px 50px'
                }}></div>

                <div className="relative max-w-6xl mx-auto px-6 py-20 text-center">
                    {/* Icon */}
                    <div className="inline-block p-4 bg-white/20 backdrop-blur-lg rounded-2xl mb-6 shadow-2xl">
                        <Scale className="w-16 h-16 text-white mx-auto" />
                    </div>

                    {/* Title */}
                    <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
                        Terms & Conditions
                    </h1>

                    {/* Subtitle */}
                    <p className="text-xl md:text-2xl text-white/90 font-light max-w-3xl mx-auto mb-8">
                        Welcome to Landmarks Realty. These Terms and Conditions govern your access to and use of our website and services.
                    </p>

                    {/* Established Badge */}
                    <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/20 backdrop-blur-lg rounded-full border border-white/30 shadow-xl">
                        <div className="w-2 h-2 rounded-full bg-white dark:bg-slate-900 animate-pulse"></div>
                        <span className="text-white font-semibold text-sm">Established: January 19, 2017</span>
                    </div>
                </div>

                {/* Bottom Wave */}
                <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0]">
                    <svg className="relative block w-full h-20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                        <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
                            className="fill-white/5"></path>
                    </svg>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-5xl mx-auto px-6 py-12">
                {/* Agreement Notice */}
                <div className="bg-gradient-to-br from-[#3B2B9A]/5 to-[#5A47D8]/5 dark:from-slate-900 dark:to-slate-900 rounded-2xl shadow-lg p-8 mb-8 border border-[#3B2B9A]/20 dark:border-slate-600">
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8] rounded-xl flex items-center justify-center shadow-lg">
                            <CheckCircle className="w-6 h-6 text-white" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-2xl font-bold text-slate-900 dark:text-indigo-300 mb-3">Agreement to Terms</h2>
                            <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                By visiting our website or using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms. These terms create clarity, protect all parties involved, and establish a transparent professional relationship from the first interaction onward.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Expandable Sections */}
                <div className="space-y-4">
                    {sections.map((section, index) => {
                        const Icon = section.icon;
                        const isExpanded = expandedSection === section.id;

                        return (
                            <div
                                key={section.id}
                                className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg overflow-hidden border border-slate-100 dark:border-slate-700 transition-all duration-300 hover:shadow-xl"
                            >
                                <button
                                    type="button"
                                    onClick={() => toggleSection(section.id)}
                                    className="w-full px-8 py-6 flex items-center justify-between text-left bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-900 hover:from-slate-100 hover:to-slate-50 dark:hover:from-slate-700 dark:hover:to-slate-900 transition-colors"
                                >
                                    <div className="flex items-center flex-1 min-w-0">
                                        <div className={`w-12 h-12 shrink-0 rounded-xl bg-gradient-to-br ${section.color} flex items-center justify-center mr-4 shadow-lg transform transition-transform ${isExpanded ? 'scale-110 rotate-6' : ''}`}>
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                        <h3 className="text-lg font-bold text-slate-900 dark:text-white">{section.title}</h3>
                                    </div>
                                    <ChevronDown
                                        className={`w-6 h-6 shrink-0 ml-2 text-slate-500 dark:text-slate-400 transform transition-transform duration-300 ${isExpanded ? 'rotate-180 text-[#3B2B9A] dark:text-indigo-300' : ''}`}
                                        aria-hidden
                                    />
                                </button>

                                <div
                                    className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[1000px]' : 'max-h-0'}`}
                                >
                                    <div className="px-8 pb-6 pt-2 border-t border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-950/90">
                                        <div className={`h-1 w-20 rounded-full bg-gradient-to-r ${section.color} mb-4 mt-4`}></div>
                                        <p className="text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line">{section.content}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Closing Message */}
                <div className="mt-12 bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8] rounded-2xl shadow-2xl p-8 md:p-12 text-white relative overflow-hidden">
                    {/* Decorative Elements */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl"></div>

                    <div className="relative z-10">
                        <h3 className="text-3xl font-bold mb-4 text-center">Questions or Clarifications?</h3>
                        <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto leading-relaxed text-center">
                            If you have questions or require clarification regarding any aspect of these terms, we encourage you to connect with us directly. Our intent is to maintain transparency, clarity, and responsible advisory throughout every engagement.
                        </p>

                        {/* Divider */}
                        <div className="w-24 h-1 bg-white/30 mx-auto mb-8 rounded-full"></div>

                        {/* Signature Section */}
                        <div className="max-w-md mx-auto text-center">
                            <p className="text-white/70 text-sm mb-3 italic">Warm regards,</p>
                            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-xl">
                                <p className="text-2xl font-bold text-white mb-1">Pranjul Mishra</p>
                                <div className="w-16 h-0.5 bg-white/40 mx-auto my-3 rounded-full"></div>
                                <p className="text-white/90 font-medium">Founder and Consultant</p>
                                <p className="text-white/80 text-sm mt-1">Landmarks Realty</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}